import React, { useState, useEffect} from 'react';
import Card from '../components/Card';
import api from '../services/api';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
// import Logo from '../components/Logo';
import mermaid from 'mermaid';

const DataModelGenerator = () => {

  const [loading, setLoading] = useState(false);
  const [requirements, setRequirements] = useState('');
  const [modelName, setModelName] = useState('');
  const [dataModel, setDataModel] = useState(null);
  const [activeTab, setActiveTab] = useState('er-diagram');
  const [generatingERDiagram, setGeneratingERDiagram] = useState(false);
  const [generatingDimensionalDiagram, setGeneratingDimensionalDiagram] = useState(false);
  const [erDiagram, setERDiagram] = useState(null);
  const [dimensionalDiagram, setDimensionalDiagram] = useState(null);
  
  
  
  // Store rendered SVG content
  const [erDiagramSVG, setErDiagramSVG] = useState(null);
  const [dimensionalDiagramSVG, setDimensionalDiagramSVG] = useState(null);

  //Session Storage
  // Load state from session storage on mount
  React.useEffect(() => {
    debugSessionStorage();
    const storedRequirements = sessionStorage.getItem('requirements');
    const storedDataModel = sessionStorage.getItem('dataModel');
    const storedERDiagram = sessionStorage.getItem('erDiagram');
    const storedDimensionalDiagram = sessionStorage.getItem('dimensionalDiagram');
    const storedErDiagramSVG = sessionStorage.getItem('erDiagramSVG');
    const storedDimensionalDiagramSVG = sessionStorage.getItem('dimensionalDiagramSVG');
    const storedActiveTab = sessionStorage.getItem('activeTab');

    if (storedRequirements) {
      setRequirements(storedRequirements);
    }
    if (storedDataModel) {
      try {
        setDataModel(JSON.parse(storedDataModel));
      } catch (e) {
        console.error("Failed to parse dataModel from session storage:", e);
        sessionStorage.removeItem('dataModel');
      }
    }
    if (storedERDiagram) {
      try {
        setERDiagram(JSON.parse(storedERDiagram));
      } catch (e) {
        console.error("Failed to parse erDiagram from session storage:", e);
        sessionStorage.removeItem('erDiagram');
      }
    }
    if (storedDimensionalDiagram) {
      try {
        setDimensionalDiagram(JSON.parse(storedDimensionalDiagram));
      } catch (e) {
        console.error("Failed to parse dimensionalDiagram from session storage:", e);
        sessionStorage.removeItem('dimensionalDiagram');
      }
    }
    if (storedErDiagramSVG) {
      setErDiagramSVG(storedErDiagramSVG);
    }
    if (storedDimensionalDiagramSVG) {
      setDimensionalDiagramSVG(storedDimensionalDiagramSVG);
    }
    if (storedActiveTab) {
      setActiveTab(storedActiveTab);
    }
  }, []);

  // Save state to session storage whenever it changes
  React.useEffect(() => {
    if (dataModel) {
      sessionStorage.setItem('dataModel', JSON.stringify(dataModel));
    } else {
      sessionStorage.removeItem('dataModel');
    }
  }, [dataModel]);

  React.useEffect(() => {
    if (erDiagram) {
      sessionStorage.setItem('erDiagram', JSON.stringify(erDiagram));
    } else {
      sessionStorage.removeItem('erDiagram');
    }
  }, [erDiagram]);

  React.useEffect(() => {
    if (dimensionalDiagram) {
      sessionStorage.setItem('dimensionalDiagram', JSON.stringify(dimensionalDiagram));
    } else {
      sessionStorage.removeItem('dimensionalDiagram');
    }
  }, [dimensionalDiagram]);

  React.useEffect(() => {
    if (erDiagramSVG) {
      sessionStorage.setItem('erDiagramSVG', erDiagramSVG);
    } else {
      sessionStorage.removeItem('erDiagramSVG');
    }
  }, [erDiagramSVG]);

  React.useEffect(() => {
    if (dimensionalDiagramSVG) {
      sessionStorage.setItem('dimensionalDiagramSVG', dimensionalDiagramSVG);
    } else {
      sessionStorage.removeItem('dimensionalDiagramSVG');
    }
  }, [dimensionalDiagramSVG]);

  React.useEffect(() => {
    sessionStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  const debugSessionStorage = () => {
  const stored = sessionStorage.getItem('structuredContent');
  
  
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      console.log('Parsed value type:', typeof parsed);
      console.log('Parsed value preview:',
        typeof parsed === 'string' ? parsed.substring(0, 100) + '...' : parsed);
    } catch (e) {
      console.log('Not valid JSON');
    }
  }
  console.log('=== END DEBUG ===');
  };

  // Initialize mermaid
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
      fontFamily: 'monospace',
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!requirements.trim()) return;
    if (!modelName.trim()) {
      alert('Please enter a name for your data model');
      return;
    }
    
    setLoading(true);
    try {
      const userId = api.getAuthenticatedUserId();
      // console.log('Using authenticated user ID for data model generation:', userId);
      console.log('Model Name:', modelName);
      const response = await api.generateDataModel(requirements, userId, modelName);
      console.log('Response:', response);
      
      if (response && response.data) {
        setDataModel(response.data);
        // Reset diagrams
        setERDiagram(null);
        setDimensionalDiagram(null);
        setErDiagramSVG(null);
        setDimensionalDiagramSVG(null);
      }
    } catch (error) {
      console.error("Error generating data model:", error);
      setDataModel({
        error: "Error generating data model. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const generateERDiagram = async () => {
    if (!dataModel) return;
    
    setGeneratingERDiagram(true);
    
    try {
      const response = await api.generateERDiagram(dataModel);
      
      if (response && response.data && response.data.mermaid_code) {
        const code = response.data.mermaid_code;
        setERDiagram({ code });
        
        try {
          const { svg } = await mermaid.render('er-diagram-' + Date.now(), code);
          setErDiagramSVG(svg);
        } catch (renderError) {
          console.error("Error rendering ER diagram:", renderError);
          setErDiagramSVG(null);
        }
      } else {
        // Fallback to simpler representation if API fails
        const fallbackCode = `erDiagram
  %% Generated from data model
  ${dataModel.entities?.map(entity => 
    `${entity.name} {\n      ${entity.attributes?.map(attr => 
      `${attr.data_type} ${attr.name} ${attr.is_primary_key ? "PK" : (attr.is_foreign_key ? "FK" : "")}`
    ).join('\n      ') || ''}\n  }`
  ).join('\n  ') || ''}`;
        
        setERDiagram({ code: fallbackCode, fallback: true });
        
        try {
          const { svg } = await mermaid.render('er-diagram-fallback-' + Date.now(), fallbackCode);
          setErDiagramSVG(svg);
        } catch (renderError) {
          console.error("Error rendering fallback ER diagram:", renderError);
          setErDiagramSVG(null);
        }
      }
    } catch (error) {
      console.error("Error generating ER diagram:", error);
      // Provide a fallback
      setERDiagram({
        error: "Error generating ER diagram. Please try again."
      });
      setErDiagramSVG(null);
    } finally {
      setGeneratingERDiagram(false);
    }
  };

  const generateDimensionalDiagram = async () => {
    if (!dataModel) return;
    
    setGeneratingDimensionalDiagram(true);
    
    try {
      const response = await api.generateDimensionalDiagram(dataModel);
      
      if (response && response.data && response.data.mermaid_code) {
        const code = response.data.mermaid_code;
        setDimensionalDiagram({ code });
        
        try {
          const { svg } = await mermaid.render('dim-diagram-' + Date.now(), code);
          setDimensionalDiagramSVG(svg);
        } catch (renderError) {
          console.error("Error rendering dimensional diagram:", renderError);
          setDimensionalDiagramSVG(null);
        }
      } else {
        // Fallback to simpler representation if API fails
        const fallbackCode = `erDiagram
  %% Dimensional Model (Fallback)
  FACT_TABLE {
      id INTEGER PK
      ${dataModel.entities?.map(entity => 
        entity.attributes?.filter(attr => !attr.is_primary_key)
          .map(attr => `${attr.name} ${attr.data_type}`)
          .join('\n      ')
      ).join('\n      ') || ''}
  }
  ${dataModel.entities?.map(entity => 
    `DIM_${entity.name.toUpperCase()} {\n      ${entity.name}_id INTEGER PK\n      ${
      entity.attributes?.filter(attr => !attr.is_primary_key)
        .map(attr => `${attr.name} ${attr.data_type}`)
        .join('\n      ') || ''
    }\n  }`
  ).join('\n  ') || ''}
  ${dataModel.entities?.map(entity => 
    `FACT_TABLE ||--o{ DIM_${entity.name.toUpperCase()} : has`
  ).join('\n  ') || ''}`;
        
        setDimensionalDiagram({ code: fallbackCode, fallback: true });
        
        try {
          const { svg } = await mermaid.render('dim-diagram-fallback-' + Date.now(), fallbackCode);
          setDimensionalDiagramSVG(svg);
        } catch (renderError) {
          console.error("Error rendering fallback dimensional diagram:", renderError);
          setDimensionalDiagramSVG(null);
        }
      }
    } catch (error) {
      console.error("Error generating dimensional diagram:", error);
      // Provide a fallback
      setDimensionalDiagram({
        error: "Error generating dimensional diagram. Please try again."
      });
      setDimensionalDiagramSVG(null);
    } finally {
      setGeneratingDimensionalDiagram(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const downloadDataModel = () => {
    if (!dataModel) return;
    
    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(dataModel, null, 2)], {type: 'application/json'});
    element.href = URL.createObjectURL(file);
    element.download = "data_model.json";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Function to convert diagram to PNG and download
  const downloadDiagramAsPNG = (svgContent, filename) => {
    if (!svgContent) return;
    
    // Create a temporary DOM element to hold the SVG
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = svgContent;
    const svgElement = tempDiv.firstChild;
    
    // Get SVG dimensions
    let width, height;
    
    // Try to get dimensions from viewBox first
    if (svgElement.getAttribute('viewBox')) {
      const viewBox = svgElement.getAttribute('viewBox').split(' ');
      width = parseFloat(viewBox[2]);
      height = parseFloat(viewBox[3]);
    }
    
    // If no viewBox, try to get from width/height attributes
    if (!width || !height) {
      width = parseFloat(svgElement.getAttribute('width')) || 1200; // Default width if not set
      height = parseFloat(svgElement.getAttribute('height')) || 800; // Default height if not set
    }
    
    // Create an image from the SVG
    const canvas = document.createElement('canvas');
    // Set a minimum size and scale up if needed
    const scale = 2; // Scale factor for better quality
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = function() {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      
      // Create download link
      canvas.toBlob(function(blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }, 'image/png', 1.0);
    };
    
    // Set explicit dimensions on the SVG element
    svgElement.setAttribute('width', width);
    svgElement.setAttribute('height', height);
    
    // Convert SVG to data URL
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
    img.src = URL.createObjectURL(svgBlob);
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-textPrimary">Data Model Generator</h1>
          {/* <Logo className="hidden md:flex" /> */}
        </div>
        
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left column - Input form */}
          <div className="lg:col-span-1">
            <Card title="Business Requirements">
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="modelName" className="form-label">
                    Data Model Name:
                  </label>
                  <input
                    type="text"
                    id="modelName"
                    className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent mb-4"
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    placeholder="Enter a descriptive name for your data model"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="requirements" className="form-label">
                    Enter your business requirements:
                  </label>
                    <textarea
                    id="requirements"
                    className="w-full px-3 py-2 border border-inputBorder rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                    rows="12"
                    value={requirements}
                    onChange={(e) => setRequirements(e.target.value)}
                    placeholder="Paste your business requirements here or write a detailed description of your data needs..."
                    required
                  />
                </div>
                
                <div className="mt-6">
                  <button
                    type="submit"
                    className="btn-primary w-full"
                    disabled={loading}
                    style={{ background: '#093878' }}
                  >
                    {loading ? 'Generating...' : 'Generate Data Model'}
                  </button>
                </div>
              </form>
            </Card>
          </div>
          
          {/* Right column - Results */}
          <div className="lg:col-span-2">
            {loading && (
              <Card>
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                </div>
              </Card>
            )}
            
            {!loading && dataModel && (
              <div>
                <div className="mb-6 bg-white shadow-sm rounded-lg overflow-hidden">
                  <div className="flex border-b border-borderLight overflow-x-auto">
                    <button
                      className={`px-4 py-3 text-sm font-medium ${activeTab === 'er-diagram' ? 'text-accent border-b-2 border-accent' : 'text-textSecondary hover:text-textPrimary'}`}
                      onClick={() => handleTabChange('er-diagram')}
                      
                    >
                      ER Diagram
                    </button>
                    <button
                      className={`px-4 py-3 text-sm font-medium ${activeTab === 'dimensional-diagram' ? 'text-accent border-b-2 border-accent' : 'text-textSecondary hover:text-textPrimary'}`}
                      onClick={() => handleTabChange('dimensional-diagram')}
                    >
                      Dimensional Model
                    </button>
                    <button
                      className={`px-4 py-3 text-sm font-medium ${activeTab === 'entities' ? 'text-accent border-b-2 border-accent' : 'text-textSecondary hover:text-textPrimary'}`}
                      onClick={() => handleTabChange('entities')}
                    >
                      Entities & Relationships
                    </button>
                    <button
                      className={`px-4 py-3 text-sm font-medium ${activeTab === 'json' ? 'text-accent border-b-2 border-accent' : 'text-textSecondary hover:text-textPrimary'}`}
                      onClick={() => handleTabChange('json')}
                    >
                      JSON Output
                    </button>
                  </div>
                  
                  <div className="p-4">
                    {activeTab === 'er-diagram' && (
                      <div>
                        <div className="mb-4">
                          {!erDiagramSVG && (
                            <button
                              className="btn-primary"
                              onClick={generateERDiagram}
                              disabled={generatingERDiagram}
                              style={{ background: '#093878' }}
                  
                            >
                              {generatingERDiagram ? 'Generating...' : 'Generate ER Diagram'}
                            </button>
                          )}
                        </div>
                        
                        {generatingERDiagram ? (
                          <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                          </div>
                        ) : erDiagram ? (
                          <div>
                            {erDiagram.error ? (
                              <p className="text-semanticFail">{erDiagram.error}</p>
                            ) : erDiagramSVG ? (
                              <>
                                <div 
                                  className="overflow-auto max-h-[500px] border border-borderLight rounded p-4 bg-white"
                                  dangerouslySetInnerHTML={{ __html: erDiagramSVG }}
                                />
                                <div className="mt-4">
                                  <details className="mb-4">
                                    <summary className="cursor-pointer text-sm font-medium text-textPrimary py-2">
                                      View Mermaid Code
                                    </summary>
                                    <SyntaxHighlighter language="text" style={docco} className="rounded-md">
                                      {erDiagram.code}
                                    </SyntaxHighlighter>
                                  </details>
                                </div>
                              </>
                            ) : (
                              <p className="text-semanticFail">Error rendering diagram. Please try again.</p>
                            )}
                            
                            <div className="mt-4 flex flex-wrap gap-2">
                              <button
                                className="btn-secondary"
                                onClick={() => {
                                  const element = document.createElement("a");
                                  const file = new Blob([erDiagram.code], {type: 'text/plain'});
                                  element.href = URL.createObjectURL(file);
                                  element.download = "er_diagram.mmd";
                                  document.body.appendChild(element);
                                  element.click();
                                  document.body.removeChild(element);
                                }}
                              >
                                Download as Mermaid
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => downloadDiagramAsPNG(erDiagramSVG, 'er_diagram.png')}
                                disabled={!erDiagramSVG}
                              >
                                Download as PNG
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-textSecondary italic">Click "Generate ER Diagram" to visualize the entity relationships.</p>
                        )}
                      </div>
                    )}
                    
                    {activeTab === 'dimensional-diagram' && (
                      <div>
                        <div className="mb-4">
                          {!dimensionalDiagramSVG && (
                            <button
                              className="btn-primary"
                              onClick={generateDimensionalDiagram}
                              disabled={generatingDimensionalDiagram}
                              style={{ background: '#093878' }}
                  
                            >
                              {generatingDimensionalDiagram ? 'Generating...' : 'Generate Dimensional Model'}
                            </button>
                          )}
                        </div>
                        
                        {generatingDimensionalDiagram ? (
                          <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                          </div>
                        ) : dimensionalDiagram ? (
                          <div>
                            {dimensionalDiagram.error ? (
                              <p className="text-semanticFail">{dimensionalDiagram.error}</p>
                            ) : dimensionalDiagramSVG ? (
                              <>
                                <div 
                                  className="overflow-auto max-h-[500px] border border-borderLight rounded p-4 bg-white"
                                  dangerouslySetInnerHTML={{ __html: dimensionalDiagramSVG }}
                                />
                                <div className="mt-4">
                                  <details className="mb-4">
                                    <summary className="cursor-pointer text-sm font-medium text-textPrimary py-2">
                                      View Mermaid Code
                                    </summary>
                                    <SyntaxHighlighter language="text" style={docco} className="rounded-md">
                                      {dimensionalDiagram.code}
                                    </SyntaxHighlighter>
                                  </details>
                                </div>
                              </>
                            ) : (
                              <p className="text-semanticFail">Error rendering diagram. Please try again.</p>
                            )}
                            
                            <div className="mt-4 flex flex-wrap gap-2">
                              <button
                                className="btn-secondary"
                                onClick={() => {
                                  const element = document.createElement("a");
                                  const file = new Blob([dimensionalDiagram.code], {type: 'text/plain'});
                                  element.href = URL.createObjectURL(file);
                                  element.download = "dimensional_diagram.mmd";
                                  document.body.appendChild(element);
                                  element.click();
                                  document.body.removeChild(element);
                                }}
                              >
                                Download as Mermaid
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => downloadDiagramAsPNG(dimensionalDiagramSVG, 'dimensional_diagram.png')}
                                disabled={!dimensionalDiagramSVG}
                              >
                                Download as PNG
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-textSecondary italic">Click "Generate Dimensional Model" to visualize the dimensional model.</p>
                        )}
                      </div>
                    )}
                    
                    {activeTab === 'entities' && (
                      <div>
                        {dataModel.error ? (
                          <p className="text-semanticFail">{dataModel.error}</p>
                        ) : (
                          <div>
                            <h3 className="text-lg font-medium text-textPrimary mb-4">Entities</h3>
                            {dataModel.entities && dataModel.entities.map((entity, index) => (
                              <div key={index} className="mb-6 border border-borderLight rounded-lg p-4">
                                <h4 className="text-md font-medium text-textPrimary">{entity.name}</h4>
                                <p className="text-sm text-textSecondary mb-2">{entity.description}</p>
                                
                                <h5 className="text-sm font-medium text-textPrimary mt-3 mb-1">Attributes:</h5>
                                <div className="bg-panelHover p-3 rounded-md overflow-x-auto">
                                  <table className="min-w-full divide-y divide-borderLight">
                                    <thead>
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Name</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Type</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">PK</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">FK</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Description</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-borderLight">
                                      {entity.attributes && entity.attributes.map((attr, attrIndex) => (
                                        <tr key={attrIndex}>
                                          <td className="px-3 py-2 whitespace-nowrap text-sm text-textPrimary">{attr.name}</td>
                                          <td className="px-3 py-2 whitespace-nowrap text-sm text-textSecondary">{attr.data_type}</td>
                                          <td className="px-3 py-2 whitespace-nowrap text-sm text-textSecondary">{attr.is_primary_key ? '✓' : ''}</td>
                                          <td className="px-3 py-2 whitespace-nowrap text-sm text-textSecondary">{attr.is_foreign_key ? '✓' : ''}</td>
                                          <td className="px-3 py-2 text-sm text-textSecondary">{attr.description}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ))}
                            
                            <h3 className="text-lg font-medium text-textPrimary mb-4 mt-8">Relationships</h3>
                            {dataModel.relationships && dataModel.relationships.length > 0 ? (
                              <div className="bg-panelHover p-4 rounded-md overflow-x-auto">
                                <table className="min-w-full divide-y divide-borderLight">
                                  <thead>
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Entity 1</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Relationship</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Entity 2</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-borderLight">
                                    {dataModel.relationships.map((rel, relIndex) => (
                                      <tr key={relIndex}>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-textPrimary">{rel.entity1}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-textSecondary">{rel.relationship_type}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-textPrimary">{rel.entity2}</td>
                                        <td className="px-3 py-2 text-sm text-textSecondary">{rel.description}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-textSecondary italic">No relationships defined.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {activeTab === 'json' && (
                      <div>
                        <div className="mb-4 flex flex-wrap gap-2">
                          <button
                            className="btn-primary"
                            onClick={downloadDataModel}
                            style={{ background: '#093878' }}
                  
                          >
                            Download Data Model (JSON)
                          </button>
                        </div>
                        <SyntaxHighlighter language="json" style={docco} className="rounded-md">
                          {JSON.stringify(dataModel, null, 2)}
                        </SyntaxHighlighter>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataModelGenerator; 